import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ButtonBase from '@mui/material/ButtonBase';
import {
  ChevronDownIcons,
  LayersIcons,
  SubtractCircleIcons,
} from '../../../../icons/icons';
import { formatItemIndex } from '../functions';
import { SkillCardProps } from '../interfaces';
import { SubSkillsDrawer } from './SubSkillsDrawer';

export const SkillCard: React.FC<SkillCardProps> = ({
  skill,
  index,
  currentPage,
  itemsPerPage,
  subSkills,
  isExpanded,
  onToggleExpand,
  onDeleteSkill,
  onDeleteSubSkill,
}) => {
  return (
    <Paper
      variant='outlined'
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: '6px',
        borderColor: isExpanded ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        p: 1.75,
        transition: 'all 0.2s ease',
        boxShadow: isExpanded
          ? '0 0 0 1px rgba(59, 130, 246, 0.4), 0 1px 2px rgba(0, 0, 0, 0.05)'
          : 'none',
        '&:hover': {
          borderColor: isExpanded ? 'primary.main' : 'action.active',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
          '& .delete-btn': {
            opacity: 1,
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1, mr: 1 }}>
          <Typography
            component='span'
            sx={{
              fontSize: '0.6875rem',
              color: 'text.secondary',
              fontFamily: 'monospace',
              bgcolor: 'action.hover',
              px: 0.75,
              py: 0.25,
              borderRadius: '4px',
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            {formatItemIndex(index, currentPage, itemsPerPage)}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={skill.label}
          >
            {skill.label}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
          {subSkills.length > 0 && (
            <ButtonBase
              onClick={() => onToggleExpand(skill.value)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.75rem',
                fontWeight: 500,
                px: 1,
                py: 0.35,
                borderRadius: '6px',
                border: '1px solid',
                transition: 'all 0.15s ease',
                bgcolor: isExpanded ? 'primary.50' : 'rgba(59, 130, 246, 0.04)',
                color: isExpanded ? 'primary.dark' : 'primary.main',
                borderColor: isExpanded ? 'primary.light' : 'rgba(59, 130, 246, 0.2)',
                '&:hover': {
                  bgcolor: 'primary.50',
                  borderColor: 'primary.main',
                },
              }}
              title={isExpanded ? 'Collapse sub-skills' : 'Expand sub-skills'}
              aria-expanded={isExpanded}
            >
              <LayersIcons style={{ width: 12, height: 12 }} />
              <Typography component='span' sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {subSkills.length}
              </Typography>
              <ChevronDownIcons
                style={{
                  width: 12,
                  height: 12,
                  transition: 'transform 0.2s ease',
                  transform: isExpanded ? 'rotate(180deg)' : 'none',
                }}
              />
            </ButtonBase>
          )}

          <IconButton
            className='delete-btn'
            size='small'
            onClick={() => onDeleteSkill(skill.value)}
            sx={{
              opacity: 0,
              transition: 'opacity 0.2s ease',
              color: 'text.secondary',
              borderRadius: '6px',
              p: 0.5,
              '& svg': { width: 16, height: 16 },
              '&:hover': {
                color: 'error.main',
                bgcolor: 'error.lighter',
              },
            }}
            title='Delete skill'
            aria-label={`Delete skill ${skill.label}`}
          >
            <SubtractCircleIcons style={{ width: 16, height: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {isExpanded && subSkills.length > 0 && (
        <SubSkillsDrawer
          subSkills={subSkills}
          onDeleteSubSkill={onDeleteSubSkill}
        />
      )}
    </Paper>
  );
};

